import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchInput from '@components/common/SearchInput';
import PageHeader from '@components/common/PageHeader';
import DataTable, {
    ColumnConfig,
    HeaderButton,
} from '@components/common/DataTable/DataTable.tsx';
import { api } from '@/api/weather/area/api.ts';
import {
    WeatherAreaListItem,
    WeatherAreaListRes,
    WeatherAreaListReq,
} from '@/api/weather/area/type.ts';
import {
    objectToQueryString,
    queryStringToObject,
    typeConverters,
} from '@utils/Utils';

const AreaList = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [filters, setFilters] = useState<WeatherAreaListReq>(() => {
        const urlFilters = queryStringToObject<WeatherAreaListReq>(
            searchParams,
            {
                nowPage: typeConverters.number,
                pageSize: typeConverters.number,
            },
        );

        return {
            pageSize: 10,
            nowPage: 1,
            pageGroup: 10,
            keyword: '',
            ...urlFilters,
        };
    });

    const updateFilter = (key: keyof WeatherAreaListReq, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            ...(key !== 'nowPage' && { nowPage: 1 }),
        }));
    };

    const handleSearch = (value: string) => {
        updateFilter('keyword', value);
    };

    const handlePageChange = (page: number) => {
        updateFilter('nowPage', page);
    };

    const handlePageSizeChange = (pageSize: number) => {
        updateFilter('pageSize', pageSize);
    };

    const [areaRes, setAreaRes] = useState<WeatherAreaListRes>();

    const fetchAreaList = useCallback(async () => {
        const response = await api.weatherAreaList(filters);
        setAreaRes(response);
    }, [filters]);

    const getCurrentFilterParams = (): string => {
        return objectToQueryString(filters, {
            defaultValues: {
                pageSize: 10,
                nowPage: 1,
                pageGroup: 10,
                keyword: '',
            },
        });
    };

    const onClickRow = (row: WeatherAreaListItem) => {
        console.log('클릭된 행:', row.areaIdx);
        const currentParams = getCurrentFilterParams();
        navigate(
            `/weather/area/${row.areaIdx}?returnUrl=${encodeURIComponent(currentParams)}`,
        );
    };

    const columns: ColumnConfig<WeatherAreaListItem>[] = [
        { key: 'areaIdx', header: 'No' },
        { key: 'regionName1', header: '시/도', clickable: true },
        { key: 'regionName2', header: '구/군' },
        { key: 'regionName3', header: '동/읍/면' },
        {
            key: 'longCode',
            header: '중기지역코드',
            render: value => value || '-',
        },
        { key: 'shortCode', header: '단기지역코드' },
        { key: 'nx', header: '격자X' },
        { key: 'ny', header: '격자Y' },
        { key: 'longitude', header: '경도', render: value => value || '-' },
        { key: 'latitude', header: '위도', render: value => value || '-' },
    ];

    const headerButtons: HeaderButton[] = [
        {
            label: '등록',
            icon: 'ri-add-line',
            className: 'btn btn-sm btn-primary btn-wave',
            onClick: () => navigate('/weather/area/add'),
        },
    ];

    useEffect(() => {
        fetchAreaList();
    }, [fetchAreaList]);

    return (
        <>
            <PageHeader
                title="기상 지역코드 관리"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '기상관리' },
                    { label: '기상 지역코드 관리', active: true },
                ]}
            />

            <div className="row mt-4">
                <div className="col-xl-12">
                    <div className="card custom-card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12">
                                    <SearchInput
                                        placeholder="시도, 구군, 동읍면을 입력해주세요."
                                        value={filters.keyword || ''}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    {areaRes && (
                        <DataTable<WeatherAreaListItem>
                            data={areaRes?.data}
                            columns={columns}
                            headerButtons={headerButtons}
                            paging={areaRes?.paging}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onClickRow={onClickRow}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default AreaList;
